import { DateTime } from 'luxon';

import { DATE_RANGE_LABEL_BY_ID } from './dateRangeOptions';
import {
  type DynamicRange,
  type DynamicRangeIntervalTimestep,
  type GeneratedReportRanges,
  isGeneratedReportRangeTypeDynamic,
  type PREDEFINED_RANGE_VALUE,
} from './predefinedRange';

/** Singular unit word for each dynamic range interval, e.g. `MONTH` -> `month`. */
const DYNAMIC_INTERVAL_LABELS: Record<DynamicRangeIntervalTimestep, string> = {
  DAY: 'day',
  WEEK: 'week',
  MONTH: 'month',
  QUARTER: 'quarter',
  YEAR: 'year',
};

/** The dynamic range intervals, in the order the pickers list them. */
export const DYNAMIC_RANGE_INTERVALS: Array<DynamicRangeIntervalTimestep> = ['DAY', 'WEEK', 'MONTH', 'QUARTER', 'YEAR'];

/** The unit word for an interval, pluralized for `count` — e.g. `MONTH` with 3 -> `months`. */
export const formatIntervalUnit = (interval: DynamicRangeIntervalTimestep, count: number): string =>
  `${DYNAMIC_INTERVAL_LABELS[interval]}${count === 1 ? '' : 's'}`;

/** One of the three dynamic range modes, with the copy the pickers show for it. */
export type DynamicRangeModeDef = {
  type: DynamicRange['type'];
  label: string;
  caption: string;
};

/** The dynamic range modes, in the order the pickers list them. */
export const DYNAMIC_RANGE_MODE_DEFS: Array<DynamicRangeModeDef> = [
  { type: 'CUSTOM_SINCE', label: 'Since', caption: 'Fixed start to the present date' },
  { type: 'CUSTOM_LAST', label: 'Last', caption: 'Rolling window, e.g. previous full week' },
  { type: 'CUSTOM_RECENT', label: 'Recent', caption: 'From the start of a period through today' },
];

export const DYNAMIC_RANGE_MODE_DEF_BY_TYPE = Object.fromEntries(
  DYNAMIC_RANGE_MODE_DEFS.map((def) => [def.type, def]),
) as Record<DynamicRange['type'], DynamicRangeModeDef>;

const formatDate = (date: DateTime): string => date.toLocaleString(DateTime.DATE_MED);

/** Sentence-case an enum-style id, e.g. `SAME_DAY_LAST_6_MONTHS` -> `Same day last 6 months`. */
const humanizeRangeId = (id: string): string => {
  const words = id.replace(/_/g, ' ').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/**
 * The label the date range picker shows for a fixed range —
 * e.g. `Aug 1, 2026 - Aug 10, 2026`.
 */
export const formatFixedRangeLabel = (startDate: DateTime, endDate: DateTime): string =>
  `${formatDate(startDate)} - ${formatDate(endDate)}`;

/**
 * The label the date range picker shows for a dynamic ("auto-updating") range —
 * e.g. `Since Jul 1, 2026`, `Last 1 month`, `Recent 3 quarters`.
 */
export const formatDynamicRangeLabel = (range: DynamicRange): string => {
  const { label } = DYNAMIC_RANGE_MODE_DEF_BY_TYPE[range.type];
  if (range.type === 'CUSTOM_SINCE') return `${label} ${formatDate(range.startDate)}`;

  return `${label} ${range.count} ${formatIntervalUnit(range.interval, range.count)}`;
};

/** Preview dates are zero-padded so the chip's width doesn't jitter as the inputs change. */
const formatPreviewDate = (date: DateTime): string => date.toFormat('LLL dd, yyyy');

/**
 * The resolved-interval preview the pickers show beneath a dynamic range's inputs.
 *
 * Ranges that always run through the present date (`Since` and `Recent`) name their end
 * `Today` — e.g. `Jul 01, 2026 - Today (Aug 11, 2026)` — while a rolling `Last` window
 * shows both of its concrete dates.
 */
export const formatDynamicRangePreview = (
  startDate: DateTime,
  endDate: DateTime,
  { endsToday = false }: { endsToday?: boolean } = {},
): string =>
  endsToday
    ? `${formatPreviewDate(startDate)} - Today (${formatPreviewDate(endDate)})`
    : `${formatPreviewDate(startDate)} - ${formatPreviewDate(endDate)}`;

/** The date-range shaped fields of a report configuration. */
export type ReportDateRangeFields = {
  dateRange?: GeneratedReportRanges;
  startOn?: string | null;
  endOn?: string | null;
  dateRangeInterval?: DynamicRangeIntervalTimestep | null;
  dateRangeIntervalCount?: number | null;
};

/**
 * Rebuild the `DynamicRange` a report configuration was saved with, so it can be
 * relabelled or re-resolved against the current date.
 *
 * Returns undefined when `dateRange` is not one of the dynamic values.
 */
export const toDynamicRange = (fields: ReportDateRangeFields): DynamicRange | undefined => {
  const { dateRange } = fields;
  if (!dateRange || !isGeneratedReportRangeTypeDynamic(dateRange)) return undefined;

  if (dateRange === 'CUSTOM_SINCE') {
    const startDate = DateTime.fromISO(fields.startOn ?? '');
    return startDate.isValid ? { type: 'CUSTOM_SINCE', startDate } : undefined;
  }

  return {
    type: dateRange,
    interval: fields.dateRangeInterval ?? 'MONTH',
    count: fields.dateRangeIntervalCount ?? 1,
  };
};

/**
 * The label the date range picker shows for a configuration's saved date range.
 *
 * Saved report lists render this so a row reads exactly like the picker the range
 * was chosen in. Returns undefined when the config carries no date range at all,
 * letting callers fall back to their own secondary text.
 */
export const getReportDateRangeLabel = (fields: ReportDateRangeFields): string | undefined => {
  const dynamicRange = toDynamicRange(fields);
  if (dynamicRange) return formatDynamicRangeLabel(dynamicRange);

  const { dateRange, startOn, endOn } = fields;

  const predefinedLabel = dateRange ? DATE_RANGE_LABEL_BY_ID[dateRange as PREDEFINED_RANGE_VALUE] : undefined;
  if (predefinedLabel) return predefinedLabel;

  if (dateRange === 'CUSTOM') {
    const start = startOn ? DateTime.fromISO(startOn) : undefined;
    const end = endOn ? DateTime.fromISO(endOn) : undefined;
    if (start?.isValid && end?.isValid) return formatFixedRangeLabel(start, end);
  }

  return dateRange ? humanizeRangeId(dateRange) : undefined;
};
